import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Meta } from 'src/components/Meta';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchPlanWorkoutDivisionResults,
  fetchPlanWorkoutForLeaderboard,
  fetchPlanWorkoutSimilarResults,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import { LeaderboardFilterButton } from './components/LeaderboardFilterButton';
import { LeaderboardMe } from './components/LeaderboardMe';
import { LeaderboardResultsList } from './components/LeaderboardResultsList';
import {
  computeMeResult,
  convertUnits,
  formatExerciseTime,
} from './components/leaderboardUtils';
import { useLeaderboardFilters } from './components/useLeaderboardFilters';

const PER_PAGE = 10;

export const LeaderboardWorkoutDivisionPage = () => {
  const { workoutId, type, division, subdivision } = useParams<{
    workoutId: string;
    type: string;
    division: string;
    subdivision?: string;
  }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get('tvMode') === 'true';
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });

  const [page, setPage] = useState(1);
  const { filters } = useLeaderboardFilters();
  const [unit, setUnit] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
    setUnit(null);
  }, [type, division, subdivision]);

  const { data: workout } = useQuery({
    queryKey: ['planWorkoutForLeaderboard', workoutId],
    queryFn: () => fetchPlanWorkoutForLeaderboard(workoutId!),
    enabled: !!workoutId,
  });

  const { data: resultsData, isLoading } = useQuery({
    queryKey: [
      'planWorkoutDivisionResults',
      workoutId,
      type,
      division,
      subdivision,
      page,
      filters,
    ],
    queryFn: () =>
      fetchPlanWorkoutDivisionResults({
        planWorkoutId: workoutId!,
        type,
        key: division,
        subkey: subdivision,
        page,
        per: PER_PAGE,
        ...filters,
      }),
    enabled: !!workoutId,
  });

  const { data: similarResults } = useQuery({
    queryKey: [
      'planWorkoutSimilarResults',
      workoutId,
      type,
      division,
      subdivision,
    ],
    queryFn: () =>
      fetchPlanWorkoutSimilarResults({
        planWorkoutId: workoutId!,
        planExerciseId: type === 'exercise' ? division : undefined,
        planBlockId: type === 'block' ? division : undefined,
        key: division,
        subkey: subdivision,
      }),
    enabled: !!workoutId,
  });

  const blocks = workout?.blocks || [];
  const exercises = blocks.flatMap((b: any) => b.exercises || []) || [];
  const currentExercise =
    type === 'exercise'
      ? exercises.find((e: any) => e.id?.toString() === division?.toString())
      : null;
  const currentBlock =
    type === 'block'
      ? blocks.find((b: any) => b.id?.toString() === division?.toString())
      : null;

  const primaryMetric = (() => {
    const fields = currentExercise?.fields || [];
    if (fields.includes('weight')) return 'weight';
    if (fields.includes('time')) return 'time';
    if (fields.includes('distance')) return 'distance';
    if (fields.includes('reps')) return 'reps';
    return null;
  })();

  const defaultUnit = (() => {
    const usesMetric = user?.usesMetric;
    if (primaryMetric === 'weight') return usesMetric ? 'kg' : 'lb';
    if (primaryMetric === 'distance') {
      const dm = currentExercise?.distance_measurement;
      if (dm === 'miles') return usesMetric ? 'km' : 'mi';
      if (dm === 'yards') return usesMetric ? 'm' : 'yd';
      if (dm === 'feet') return usesMetric ? 'm' : 'ft';
    }
    return null;
  })();

  const activeUnit = unit || defaultUnit;

  const unitOptions = (() => {
    if (primaryMetric === 'weight') return ['lb', 'kg'];
    if (primaryMetric === 'distance') {
      const dm = currentExercise?.distance_measurement;
      if (dm === 'miles') return ['mi', 'km'];
      if (dm === 'yards') return ['yd', 'm'];
      if (dm === 'feet') return ['ft', 'm'];
    }
    return null;
  })();

  const scoreType = currentBlock?.score_type || workout?.score_type;
  const isNonNumeric =
    scoreType === 'rounds_reps' ||
    scoreType === 'time' ||
    primaryMetric === 'time';

  const convertResult = (result: any) => {
    const r = { ...result };
    if (currentExercise && activeUnit) {
      if (primaryMetric === 'weight') {
        r.value =
          Math.round(convertUnits(Number(r.value), 'lb', activeUnit) * 100) /
          100;
      } else if (primaryMetric === 'distance') {
        r.value =
          Math.round(convertUnits(Number(r.value), 'ft', activeUnit) * 100) /
          100;
      } else if (primaryMetric === 'time') {
        r.value = formatExerciseTime(Number(r.value));
      }
    } else {
      if (scoreType === 'rounds_reps') {
        r.value = `${r.value} rounds, ${r.subvalue || 0} reps`;
      } else if (scoreType === 'time') {
        r.value = formatExerciseTime(Number(r.value));
      }
    }
    return r;
  };

  const results = (resultsData?.results || []).map(convertResult);
  const total = resultsData?.meta?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const convertedSimilar = (similarResults || []).map(convertResult);

  const me = computeMeResult(user?.id, convertedSimilar, total, false);

  const hasNoFilters =
    !filters.ageMin &&
    !filters.ageMax &&
    !filters.gender &&
    !filters.locationId;

  const showMe = !!me && hasNoFilters;

  const divisionLabel = (() => {
    if (currentBlock) {
      const blockIndex = blocks.indexOf(currentBlock);
      const blockName =
        currentBlock.name || `Block ${String.fromCharCode(65 + blockIndex)}`;
      const rxLabel =
        subdivision === 'rx' || division === 'rx' ? 'Rx' : 'Scaled';
      return `${blockName} - ${rxLabel}`;
    }
    if (currentExercise) {
      return currentExercise.exercise?.name || currentExercise.name;
    }
    const isRx = division === 'rx' || subdivision === 'rx';
    return `${isRx ? 'Rx' : 'Scaled'}`;
  })();

  const buildDivisionPills = () => {
    const pills: {
      key: string;
      label: string;
      path: string;
      active: boolean;
    }[] = [];

    if (workout?.score_type) {
      pills.push({
        key: 'rx-rx',
        label: `Rx`,
        path: `/leaderboards/public/workout/${workoutId}/rx/rx`,
        active: type === 'rx' && division === 'rx',
      });
      pills.push({
        key: 'rx-scaled',
        label: `Scaled`,
        path: `/leaderboards/public/workout/${workoutId}/rx/scaled`,
        active: type === 'rx' && division === 'scaled',
      });
    }

    blocks.forEach((block: any, index: number) => {
      if (block.text_block && block.score_type) {
        const blockName =
          block.name || `Block ${String.fromCharCode(65 + index)}`;
        pills.push({
          key: `block-${block.id}-rx`,
          label: `${blockName} - Rx`,
          path: `/leaderboards/public/workout/${workoutId}/block/${block.id}/rx`,
          active:
            type === 'block' &&
            division === block.id?.toString() &&
            subdivision === 'rx',
        });
        pills.push({
          key: `block-${block.id}-scaled`,
          label: `${blockName} - Scaled`,
          path: `/leaderboards/public/workout/${workoutId}/block/${block.id}/scaled`,
          active:
            type === 'block' &&
            division === block.id?.toString() &&
            subdivision === 'scaled',
        });
      }
      (block.exercises || []).forEach((exercise: any) => {
        pills.push({
          key: `exercise-${exercise.id}`,
          label: exercise.exercise?.name || exercise.name,
          path: `/leaderboards/public/workout/${workoutId}/exercise/${exercise.id}`,
          active: type === 'exercise' && division === exercise.id?.toString(),
        });
      });
    });

    return pills;
  };

  const pills = buildDivisionPills();

  return (
    <>
      <Meta title={divisionLabel} />
      <AppFrameConfig
        id="leaderboard-workout-division"
        containerMaxWidth="lg"
        title={divisionLabel}
        backButtonLinkTo={`/leaderboards/public/workout/${workoutId}`}
        backButtonTitle={workout?.name || ''}
      />
      <Container>
        {pills.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 2,
              overflowX: 'auto',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.default',
              zIndex: 5,
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {pills.map((pill) => (
              <Chip
                key={pill.key}
                label={pill.label}
                color={pill.active ? 'primary' : 'default'}
                onClick={() => navigate(pill.path)}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ mx: 2, my: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{divisionLabel}</Typography>
            {unitOptions && (
              <DropdownMenu
                trigger={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <Typography variant="h6">{activeUnit}</Typography>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      <Icon icon={faChevronDown} />
                    </Box>
                  </Box>
                }
              >
                {unitOptions.map((opt) => (
                  <ListItem
                    key={opt}
                    primary={opt}
                    onClick={() => setUnit(opt)}
                  />
                ))}
              </DropdownMenu>
            )}
          </Box>
          {currentExercise?.summary && (
            <Typography variant="body2" color="text.secondary">
              {activeUnit && ['lb', 'mi', 'yd', 'ft'].includes(activeUnit)
                ? currentExercise.summary
                : currentExercise.summary_metric || currentExercise.summary}
            </Typography>
          )}
        </Box>

        {showMe && <LeaderboardMe me={me} showNextTier={!isNonNumeric} />}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <LeaderboardResultsList
              results={results}
              tvMode={tvMode}
              numeric={!isNonNumeric}
              label={divisionLabel}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('pagination.page', { page })}
              </Typography>
              <IconButton
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <Icon icon={faChevronLeft} />
              </IconButton>
              <IconButton
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <Icon icon={faChevronRight} />
              </IconButton>
            </Box>
          </>
        )}

        <LeaderboardFilterButton
          availableFilters={['location', 'age', 'gender']}
        />
      </Container>
    </>
  );
};
